import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Sparkles,
  Leaf,
  Heart,
  Award,
  Star,
  MapPin,
  Phone,
  Instagram,
  Facebook,
  ArrowUpRight,
  Clock,
  ShoppingBag,
  BookOpen,
  Calendar,
} from "lucide-react";
import axios from "axios";
import { RESTAURANT, POPULAR_DISHES, IMAGES } from "../data/menu";
import { RibbonBanner, PanAfricanRibbon } from "../components/Ribbon";
import { Wordmark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ───────────────────────── Hero ───────────────────────── */
const Hero = () => (
  <section
    className="relative min-h-[100vh] pt-[180px] pb-12 md:pt-[180px] md:pb-16 bg-[#0A0A0A] overflow-hidden"
    data-testid="home-hero"
  >
    {/* background subtle texture */}
    <div
      className="absolute inset-0 opacity-[0.06]"
      style={{
        backgroundImage:
          "radial-gradient(circle at 20% 30%, #D4A017 0%, transparent 50%)",
      }}
    />
    <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
      {/* Left: copy */}
      <div className="relative z-10">
        <h1 className="font-impact text-white text-[60px] sm:text-[80px] lg:text-[112px] leading-[0.88] uppercase tracking-tight animate-fade-in-up">
          <span className="block">Authentic</span>
          <span className="block text-[#D4A017]">Nigerian</span>
          <span className="block">Flavours</span>
        </h1>
        <div className="mt-7 max-w-xl animate-fade-in-up delay-200">
          <p className="font-display italic text-[#16A34A] text-2xl md:text-3xl leading-snug">
            Bold Taste. Rich Culture.
            <br />
            Unforgettable Experience.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-3 animate-fade-in-up delay-300">
          <Link
            to="/menu"
            className="inline-flex items-center gap-3 bg-[#D4A017] hover:bg-[#F0BE3F] text-black font-bold uppercase tracking-[0.12em] px-7 py-4 text-sm rounded-full transition-all shadow-lg shadow-[#D4A017]/20"
            data-testid="hero-view-menu"
          >
            <BookOpen size={16} /> View Menu
          </Link>
          <Link
            to="/book"
            className="inline-flex items-center gap-3 bg-[#006400] hover:bg-[#0E7A36] text-white font-bold uppercase tracking-[0.12em] px-7 py-4 text-sm rounded-full transition-all shadow-lg shadow-[#006400]/30"
            data-testid="hero-book-table"
          >
            <Calendar size={16} /> Book a Table
          </Link>
        </div>

        <div className="mt-10 flex items-center gap-5 animate-fade-in-up delay-400">
          <div className="flex items-center gap-1 text-[#D4A017]">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={15} fill="#D4A017" />
            ))}
          </div>
          <span className="text-white/70 text-sm">
            Loved across Milton Keynes and beyond
          </span>
        </div>
      </div>

      {/* Right: food showcase */}
      <div className="relative animate-fade-in delay-200">
        <div className="relative aspect-square w-full max-w-[640px] mx-auto rounded-full overflow-hidden border-[6px] border-[#D4A017]/30 shadow-[0_30px_80px_rgba(212,160,23,0.25)]">
          <img
            src={IMAGES.plating}
            alt="Aradelá Sharing Platter — Nigerian dishes"
            className="w-full h-full object-cover"
            fetchpriority="high"
          />
        </div>
      </div>
    </div>
  </section>
);

/* ──────────────── Why Choose Us — yellow band ──────────────── */
const WhyChooseUs = () => {
  const features = [
    { icon: Award, title: "Authentic Recipes", desc: "Prepared with traditional Nigerian spices." },
    { icon: Leaf, title: "Fresh Ingredients", desc: "Quality ingredients, bold flavours." },
    { icon: Sparkles, title: "Great Experience", desc: "Warm hospitality, every time." },
    { icon: Heart, title: "Made With Love", desc: "Food that feels like home." },
  ];
  return (
    <section
      className="bg-[#D4A017] py-12 md:py-14 border-y-4 border-black"
      data-testid="home-why-choose-us"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-6 md:gap-x-10">
        {features.map((f, idx) => (
          <div
            key={f.title}
            className="flex items-center gap-4 group"
            data-testid={`feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="shrink-0 w-14 h-14 rounded-full bg-black flex items-center justify-center text-[#D4A017] group-hover:bg-[#8B0000] group-hover:text-white transition-colors">
              <f.icon size={22} />
            </div>
            <div>
              <div className="font-impact uppercase text-black text-base md:text-lg leading-tight tracking-wide">
                {f.title}
              </div>
              <div className="text-black/75 text-xs md:text-sm leading-snug mt-1">
                {f.desc}
              </div>
            </div>
            {idx < features.length - 1 && (
              <div className="hidden lg:block w-px h-12 bg-black/20 ml-auto" />
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ──────────────── Bento cards: Menu / About / Contact ──────────────── */
const BentoCards = () => (
  <section className="bg-[#F5F1E8] py-20 md:py-28" data-testid="home-bento">
    <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-7">
      <MenuCard />
      <AboutCard />
      <ContactCard />
    </div>
  </section>
);

const SpiceStrip = ({ src }) => (
  <div className="absolute top-0 left-0 right-0 h-20 overflow-hidden">
    <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60" />
  </div>
);

const MenuCard = () => (
  <article
    className="bg-white rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-black/5 flex flex-col"
    data-testid="bento-menu-card"
  >
    <div className="relative h-44 bg-black">
      <SpiceStrip src={IMAGES.suya} />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white pt-3">
        <Wordmark size="md" />
      </div>
      <div className="absolute -bottom-3 left-0 right-0 flex justify-center">
        <RibbonBanner color="#8B0000">Menu</RibbonBanner>
      </div>
    </div>
    <div className="p-7 pt-10 flex-1 flex flex-col">
      <p className="font-display italic text-[#8B0000] text-xl md:text-2xl text-center leading-snug mb-5">
        A taste of Nigeria
      </p>

      <div className="flex items-center gap-1 text-[#D4A017] mb-3">
        <Star size={14} fill="#D4A017" />
        <span className="font-impact uppercase text-black text-sm tracking-wider">
          Popular Dishes
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-5">
        {POPULAR_DISHES.slice(0, 3).map((d) => (
          <div key={d.name} className="text-center" data-testid={`popular-thumb-${d.name.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="aspect-square rounded-xl overflow-hidden mb-2 border border-black/10">
              <img src={d.image} alt={d.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <div className="font-impact uppercase text-[10px] text-black leading-tight">
              {d.name.split(" ").slice(0, 2).join(" ")}
            </div>
            <div className="text-[#8B0000] font-bold text-xs">£{d.price.toFixed(2)}</div>
          </div>
        ))}
      </div>

      <ul className="space-y-2 text-sm text-black/80 mb-6">
        {["Appetisers", "Traditional Mains", "Brunch", "Suya Specials", "Sharing Platters", "Soups", "Sides", "Drinks"].map((c) => (
          <li
            key={c}
            className="flex items-center justify-between py-1.5 border-b border-black/5 last:border-b-0"
          >
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#D4A017] rounded-full" />
              <span className="font-impact uppercase text-xs tracking-wider">{c}</span>
            </span>
            <ChevronRight size={14} className="text-black/40" />
          </li>
        ))}
      </ul>

      <Link
        to="/menu"
        className="mt-auto block text-center py-3.5 border-2 border-[#006400] text-[#006400] font-impact uppercase text-sm tracking-wider rounded-full hover:bg-[#006400] hover:text-white transition-colors"
        data-testid="bento-view-full-menu"
      >
        View Full Menu
      </Link>
    </div>
  </article>
);

const AboutCard = () => (
  <article
    className="bg-white rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-black/5 flex flex-col"
    data-testid="bento-about-card"
  >
    <div className="relative h-44 bg-black">
      <SpiceStrip src={IMAGES.plating} />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white pt-3">
        <Wordmark size="md" />
      </div>
      <div className="absolute -bottom-3 left-0 right-0 flex justify-center">
        <RibbonBanner color="#D4A017" textColor="#0A0A0A">About Us</RibbonBanner>
      </div>
    </div>
    <div className="p-7 pt-10 flex-1 flex flex-col">
      <p className="font-display italic text-[#D4A017] text-xl md:text-2xl text-center leading-snug mb-5">
        Our Story
      </p>
      <p className="text-black/75 leading-relaxed text-center text-sm md:text-base">
      Aradelá Nigerian Restaurant brings the rich, vibrant flavours of Nigeria to Milton Keynes.
      </p>
      <p className="text-black/75 leading-relaxed text-center text-sm md:text-base mt-3">
        Every dish is prepared with authentic ingredients, bold spices, and a passion for quality.
      </p>
      <p className="text-black/75 leading-relaxed text-center text-sm md:text-base mt-3">
        From classic Jollof Rice to signature Suya, we create meals that feel like home while delivering a premium dining experience.
      </p>

      <div className="grid grid-cols-4 gap-2 mt-6 mb-6">
        {[
          { icon: Award, label: "Authentic Flavours" },
          { icon: Leaf, label: "Quality Ingredients" },
          { icon: Heart, label: "Made With Love" },
          { icon: Sparkles, label: "Great Service" },
        ].map((v) => (
          <div key={v.label} className="flex flex-col items-center text-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[#8B0000] text-white flex items-center justify-center">
              <v.icon size={16} />
            </div>
            <span className="font-impact uppercase text-[9px] text-black leading-tight">
              {v.label}
            </span>
          </div>
        ))}
      </div>

      <Link
        to="/about"
        className="mt-auto block text-center py-3.5 border-2 border-[#8B0000] text-[#8B0000] font-impact uppercase text-sm tracking-wider rounded-full hover:bg-[#8B0000] hover:text-white transition-colors"
        data-testid="bento-read-more"
      >
        Read Our Story
      </Link>
    </div>
  </article>
);

const ContactCard = () => (
  <article
    className="bg-white rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-black/5 flex flex-col"
    data-testid="bento-contact-card"
  >
    <div className="relative h-44 bg-black">
      <SpiceStrip src={IMAGES.bar} />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white pt-3">
        <Wordmark size="md" />
      </div>
      <div className="absolute -bottom-3 left-0 right-0 flex justify-center">
        <RibbonBanner color="#006400">Contact Us</RibbonBanner>
      </div>
    </div>
    <div className="p-7 pt-10 flex-1 flex flex-col">
      <p className="font-display italic text-[#006400] text-xl md:text-2xl text-center leading-snug mb-5">
        We'd love to hear from you
      </p>

      <ul className="space-y-4 mb-5">
        <li className="flex gap-3">
          <div className="w-9 h-9 rounded-full bg-[#8B0000]/10 flex items-center justify-center shrink-0">
            <MapPin size={15} className="text-[#8B0000]" />
          </div>
          <div className="text-sm text-black/80 leading-snug">
            19 Stratford Road<br />Wolverton<br />Milton Keynes<br />MK12 5LJ
          </div>
        </li>
        <li className="flex gap-3 items-center">
          <div className="w-9 h-9 rounded-full bg-[#8B0000]/10 flex items-center justify-center shrink-0">
            <Phone size={15} className="text-[#8B0000]" />
          </div>
          <a href={`tel:${RESTAURANT.phoneRaw}`} className="text-black font-bold">
            {RESTAURANT.phone}
          </a>
        </li>
        <li className="flex gap-3">
          <div className="w-9 h-9 rounded-full bg-[#8B0000]/10 flex items-center justify-center shrink-0">
            <Clock size={15} className="text-[#8B0000]" />
          </div>
          <div className="flex-1 text-xs text-black/80 space-y-0.5">
            <div className="font-impact uppercase text-black tracking-wider text-[10px] mb-1">
              Opening Hours
            </div>
            {RESTAURANT.hours.map((h) => (
              <div key={h.day} className="flex justify-between">
                <span>{h.day}</span>
                <span className="text-black/65">{h.time}</span>
              </div>
            ))}
          </div>
        </li>
      </ul>

      <a
        href={RESTAURANT.mapsLink}
        target="_blank"
        rel="noreferrer"
        className="block rounded-xl overflow-hidden border border-black/10 mb-5 relative"
        data-testid="bento-map-link"
      >
        <iframe
          title="Aradela map"
          src={RESTAURANT.mapsEmbed}
          className="w-full h-32 pointer-events-none"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        <div className="absolute inset-0 flex items-end justify-center pb-3 bg-gradient-to-t from-black/40 to-transparent">
          <span className="bg-black text-white text-[11px] font-impact uppercase tracking-wider px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
            <MapPin size={11} /> Get Directions
          </span>
        </div>
      </a>

      <div className="mt-auto flex items-center justify-between">
        <span className="font-impact uppercase text-[11px] text-black tracking-wider">Follow Us</span>
        <div className="flex gap-2">
          <a
            href={RESTAURANT.social.facebook}
            target="_blank"
            rel="noreferrer"
            className="w-9 h-9 rounded-full bg-[#006400] text-white flex items-center justify-center hover:bg-[#0E7A36] transition-colors"
            data-testid="bento-fb"
            aria-label="Facebook"
          >
            <Facebook size={14} />
          </a>
          <a
            href={RESTAURANT.social.instagram}
            target="_blank"
            rel="noreferrer"
            className="w-9 h-9 rounded-full bg-[#006400] text-white flex items-center justify-center hover:bg-[#0E7A36] transition-colors"
            data-testid="bento-ig"
            aria-label="Instagram"
          >
            <Instagram size={14} />
          </a>
          <a
            href={RESTAURANT.social.tiktok}
            target="_blank"
            rel="noreferrer"
            className="w-9 h-9 rounded-full bg-[#006400] text-white flex items-center justify-center hover:bg-[#0E7A36] transition-colors"
            data-testid="bento-tiktok"
            aria-label="TikTok"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9a8.16 8.16 0 0 0 4.77 1.52V7.1a4.85 4.85 0 0 1-1.84-.41z"/>
            </svg>
          </a>
        </div>
      </div>
    </div>
  </article>
);

/* ──────────────── QR Section (yellow / red / green) ──────────────── */
const QRSection = () => {
  const url = typeof window !== "undefined" ? window.location.origin : "https://www.aradelarestaurant.co.uk";
  const make = (path) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=300x300&bgcolor=ffffff&color=000000&qzone=2&data=${encodeURIComponent(
      `${url}${path}`
    )}`;
  const cards = [
    {
      title: "Scan For Menu",
      sub: "View our full menu",
      cta: "View Our Full Menu",
      path: "/menu",
      bg: "#D4A017",
      text: "#0A0A0A",
      icon: BookOpen,
    },
    {
      title: "Scan to Order Now",
      sub: "Quick & easy ordering",
      cta: "Order Fresh & Fast",
      path: "/menu#order",
      bg: "#8B0000",
      text: "#FFFFFF",
      icon: ShoppingBag,
    },
    {
      title: "Scan to Leave a Review",
      sub: "Share your experience",
      cta: "Thank You!",
      path: "/contact",
      bg: "#006400",
      text: "#FFFFFF",
      icon: Star,
    },
  ];

  return (
    <section className="bg-[#F5F1E8] pb-20 md:pb-28" data-testid="home-qr-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex items-center justify-center gap-3 mb-12">
          <span className="hidden md:block h-px w-20 bg-[#8B0000]" />
          <h2 className="font-impact uppercase text-3xl md:text-5xl tracking-wider text-center">
            <span className="text-[#006400]">Scan.</span>{" "}
            <span className="text-[#8B0000]">Order.</span>{" "}
            <span className="text-[#D4A017]">Enjoy.</span>
          </h2>
          <span className="hidden md:block h-px w-20 bg-[#8B0000]" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((c) => (
            <div
              key={c.title}
              className="rounded-3xl p-7 relative overflow-hidden shadow-xl"
              style={{ background: c.bg, color: c.text }}
              data-testid={`qr-card-${c.title.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {/* watermark chili shape */}
              <div className="absolute -right-6 -bottom-6 opacity-10 text-[160px] leading-none select-none pointer-events-none">
                🌶
              </div>

              <div className="flex items-start gap-3 mb-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                  style={{
                    background: c.text === "#FFFFFF" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
                  }}
                >
                  <c.icon size={20} />
                </div>
                <div>
                  <div
                    className="font-impact uppercase tracking-wider text-xs opacity-70"
                  >
                    Scan to
                  </div>
                  <div className="font-impact uppercase text-2xl leading-tight tracking-wide">
                    {c.title.replace(/^scan (to|for) /i, "")}
                  </div>
                </div>
              </div>

              <a
                href={`${url}${c.path}`}
                className="block bg-white p-4 rounded-2xl mb-5"
                data-testid={`qr-image-link-${c.path.replace(/[^a-z]/g, "")}`}
              >
                <img src={make(c.path)} alt={c.title} loading="lazy" decoding="async" className="w-full h-auto" />
              </a>

              <Link
                to={c.path === "/menu#order" ? "/menu" : c.path}
                className="block text-center font-impact uppercase tracking-wider text-sm pb-1 border-b-2"
                style={{ borderColor: c.text }}
                data-testid={`qr-cta-${c.cta.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {c.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ──────────────── Testimonials ──────────────── */
const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then((res) => setReviews(res.data))
      .catch(() => setReviews([]));
  }, []);

  useEffect(() => {
    if (reviews.length === 0) return;
    const id = setInterval(() => setActive((a) => (a + 1) % reviews.length), 6000);
    return () => clearInterval(id);
  }, [reviews]);

  if (!reviews.length) return null;
  const current = reviews[active];

  return (
    <section
      className="py-20 md:py-28 bg-black relative overflow-hidden"
      data-testid="home-testimonials"
    >
      <PanAfricanRibbon className="top-0 left-0 right-0 h-2" />
      <div className="max-w-5xl mx-auto px-5 md:px-10 text-center">
        <span className="text-[#D4A017] uppercase tracking-[0.3em] text-xs font-impact">
          Loved by guests
        </span>
        <h2 className="font-impact text-white text-4xl md:text-6xl mt-3 mb-10 uppercase tracking-wide">
          What our <span className="text-[#D4A017]">Customers</span> say
        </h2>

        <p
          key={current.id}
          className="text-white text-xl md:text-3xl leading-snug max-w-4xl mx-auto animate-fade-in italic"
          data-testid="testimonial-text"
        >
          "{current.text}"
        </p>
        <div className="mt-8 flex items-center justify-center gap-1 text-[#D4A017]">
          {[...Array(current.rating)].map((_, i) => (
            <Star key={i} size={16} fill="#D4A017" />
          ))}
        </div>
        <div className="mt-3 text-white font-impact uppercase tracking-wider">
          {current.name}
        </div>
        <div className="text-white/50 text-xs uppercase tracking-[0.3em]">
          {current.location}
        </div>

        <div className="mt-8 flex justify-center gap-2">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`h-2 rounded-full transition-all ${
                i === active ? "bg-[#D4A017] w-10" : "bg-white/20 w-2 hover:bg-white/50"
              }`}
              data-testid={`testimonial-dot-${i}`}
              aria-label={`Show testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>
      <PanAfricanRibbon className="bottom-0 left-0 right-0 h-2" />
    </section>
  );
};

/* ──────────────── Instagram Strip ──────────────── */
const InstagramFeed = () => {
  const tiles = [
    IMAGES.mealForTwo,
    IMAGES.mixedGrill,
    IMAGES.wingsPlantain,
    IMAGES.explorerPlatter,
    IMAGES.pepperedWings,
    IMAGES.bar,
  ];
  return (
    <section className="py-20 bg-[#F5F1E8]" data-testid="home-instagram">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-[#8B0000] uppercase tracking-[0.3em] text-xs font-impact">
              Follow the journey
            </span>
            <h2 className="font-impact uppercase text-black text-4xl md:text-5xl mt-2 tracking-wide">
              On <span className="text-[#D4A017]">Instagram</span>
            </h2>
          </div>
          <a
            href={RESTAURANT.social.instagram}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 font-impact uppercase text-sm tracking-wider text-[#8B0000] hover:text-black"
            data-testid="instagram-follow-link"
          >
            <Instagram size={16} /> @aradelarestaurant <ArrowUpRight size={14} />
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {tiles.map((img, i) => (
            <a
              key={i}
              href={RESTAURANT.social.instagram}
              target="_blank"
              rel="noreferrer"
              className="aspect-square overflow-hidden group relative block rounded-2xl"
              data-testid={`instagram-tile-${i}`}
            >
              <img
                src={img}
                alt="Aradela on Instagram"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 flex items-center justify-center transition-colors">
                <Instagram size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page" className="bg-[#F5F1E8]">
      <Hero />
      <WhyChooseUs />
      <Testimonials />
      <InstagramFeed />
    </main>
  );
}
